import { useEffect, useRef, useState } from 'react';
import {
  countsToAssignments,
  getNextAssignments,
  assignmentsToCounts,
  normalizeCounts,
  serializeCounts,
} from './part-utils';

interface UseVisualFractionStateOptions {
  widgetId: string;
  divisions: number;
  filledCount: number;
  answer: readonly number[];
  value: number[] | undefined;
  onChange: (widgetId: string, value: number[]) => void;
}

interface UseVisualFractionStateResult {
  partAssignments: Array<number | null>;
  handlePartClick: (partIndex: number) => void;
}

/**
 * Encapsulates all interactive state for VisualFraction:
 * - syncing external `value` prop → internal slot assignments
 * - cycling group assignments on click
 * - emitting onChange with the updated counts
 */
export function useVisualFractionState({
  widgetId,
  divisions,
  filledCount,
  answer,
  value,
  onChange,
}: UseVisualFractionStateOptions): UseVisualFractionStateResult {
  const [partAssignments, setPartAssignments] = useState<Array<number | null>>(
    () => countsToAssignments(normalizeCounts(value, answer), divisions, filledCount),
  );

  // Track the last value we emitted ourselves so we can skip the redundant
  // useEffect sync that would otherwise fire after every onChange call.
  const lastInternalValueRef = useRef<string | null>(null);

  // Stable string key — avoids re-running the effect when the parent passes
  // a fresh array reference with the same contents.
  const valueKey = serializeCounts(value);

  useEffect(() => {
    if (valueKey !== null && valueKey === lastInternalValueRef.current) return;
    lastInternalValueRef.current = null;
    setPartAssignments(
      countsToAssignments(normalizeCounts(value, answer), divisions, filledCount),
    );
  }, [answer, divisions, filledCount, valueKey]);

  const handlePartClick = (partIndex: number) => {
    if (partIndex < filledCount) return;

    const updated = getNextAssignments(partAssignments, partIndex, answer.length);
    const nextCounts = assignmentsToCounts(updated, answer.length);
    lastInternalValueRef.current = serializeCounts(nextCounts);
    setPartAssignments(updated);
    onChange(widgetId, nextCounts);
  };

  return { partAssignments, handlePartClick };
}
